import { type FirebaseApp, initializeApp } from "firebase/app";
import {
  type Auth,
  browserLocalPersistence,
  getAuth,
  setPersistence,
} from "firebase/auth";
import { safeConsole } from "./src/common/utils/safeConsole";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// storageBucket and messagingSenderId are optional for auth-only usage
const hasFirebaseConfig = Boolean(
  firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId &&
    firebaseConfig.appId,
);

let app: FirebaseApp | undefined;
let auth: Auth | undefined;

// Firebase is optional for local dev (email/password uses backend /auth/simple-login).
// If config is missing or invalid, we disable Firebase-based login methods.
if (hasFirebaseConfig) {
  try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    // Persist the Firebase session across reloads/tabs so it can be used to
    // silently re-mint the backend JWT (see silentReauth). This is Firebase's
    // default, but set it explicitly so the silent-re-auth flow doesn't depend
    // on an implicit default that a future SDK change could alter.
    void setPersistence(auth, browserLocalPersistence).catch((error) => {
      safeConsole.warn(
        "authentication-lifecycle",
        "Failed to set Firebase auth persistence.",
        error,
      );
    });
  } catch (error) {
    safeConsole.warn(
      "authentication-lifecycle",
      "Firebase init failed; continuing without Firebase auth.",
      error,
    );
    app = undefined;
    auth = undefined;
  }
} else {
  safeConsole.warn(
    "authentication-lifecycle",
    "Firebase config missing; continuing without Firebase auth.",
  );
}

/**
 * Check if Firebase is properly configured and initialized.
 * Use this before attempting Firebase operations.
 */
export function isFirebaseReady(): boolean {
  return auth !== undefined;
}

/**
 * Get the Firebase Auth instance, throwing a clear error if unavailable.
 * Use this when Firebase auth is required for an operation.
 * @throws Error if Firebase is not configured or failed to initialize
 */
export function getFirebaseAuth(): Auth {
  if (!auth) {
    throw new Error(
      "Firebase Auth is not available. Ensure VITE_FIREBASE_* environment variables are configured.",
    );
  }
  return auth;
}
